import { useCallback, useEffect, useState } from 'react';
import { getOrderSummaryApi } from 'api/hallazgoFlujo';
import {
  patchOrderHeaderApi,
  patchOrderItemApi,
  encenderGanttApi,
  addDependenciaApi,
  removeDependenciaApi,
  crearItemApi,
} from 'api/odtGantt';

// Datos y mutaciones de la carta Gantt de una ODT. Carga el summary
// (GET /orders/:id/summary) y expone las tareas, las dependencias y la cabecera
// (fecha_comprometida, modo_gantt). Cada mutación golpea su endpoint y recarga el
// summary para quedar en sync con lo que el backend recalculó (el reparto
// automático al encender deja fechas que no vinieron del cliente).
//
// El summary es la fuente de verdad: no se hace optimistic update para no
// desincronizar la vista de lo que el reparto/validaciones del backend decidieron.
export function useOrderGantt(idOrder) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const recargar = useCallback(async () => {
    if (!idOrder) return;
    setLoading(true);
    setError(null);
    const data = await getOrderSummaryApi(idOrder);
    if (data && typeof data.id === 'number') {
      setSummary(data);
    } else {
      setError(data?.message || 'No se pudo cargar la ODT');
    }
    setLoading(false);
  }, [idOrder]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  // Enciende la carta Gantt (setea modo_gantt=true + reparte fechas). La fecha
  // comprometida es obligatoria si la ODT no la tiene todavía.
  const encender = useCallback(
    async (fechaComprometida) => {
      const payload = fechaComprometida ? { fecha_comprometida: fechaComprometida } : {};
      const res = await encenderGanttApi(idOrder, payload);
      await recargar();
      return res;
    },
    [idOrder, recargar]
  );

  // Apaga el modo (vuelve al nivel simple). No borra fechas ni dependencias.
  const apagar = useCallback(async () => {
    const res = await patchOrderHeaderApi(idOrder, { modo_gantt: false });
    await recargar();
    return res;
  }, [idOrder, recargar]);

  // Edita la planificación de UNA tarea (fecha, responsable, jerarquía, orden) vía
  // el endpoint dedicado — sin reenviar el resto ni tocar su equipo/descripción.
  const actualizarItem = useCallback(
    async (idItem, campos) => {
      const res = await patchOrderItemApi(idOrder, idItem, campos);
      await recargar();
      return res;
    },
    [idOrder, recargar]
  );

  // Crea UNA tarea nueva. Se recarga el resumen completo y no se hace optimistic
  // update: el backend decide la ubicación (sort_order) y puede correr el orden
  // de las tareas que ya estaban.
  const crearItem = useCallback(
    async (payload) => {
      const res = await crearItemApi(idOrder, payload);
      await recargar();
      return res;
    },
    [idOrder, recargar]
  );

  // Reordena las tareas: persiste el sort_order nuevo de las que cambiaron de
  // posición, y recarga una sola vez al final. `tareasOrdenadas` es el array en
  // el orden nuevo.
  const reordenar = useCallback(
    async (tareasOrdenadas) => {
      const previo = new Map((summary?.items ?? []).map((t) => [t.id, t.sort_order]));
      const cambiadas = (tareasOrdenadas || [])
        .map((t, i) => ({ id: t.id, sort_order: i }))
        .filter((t) => previo.get(t.id) !== t.sort_order);
      for (const t of cambiadas) {
        // eslint-disable-next-line no-await-in-loop
        await patchOrderItemApi(idOrder, t.id, { sort_order: t.sort_order });
      }
      await recargar();
    },
    [idOrder, summary, recargar]
  );

  const agregarDep = useCallback(
    async (idItem, idPredecesor) => {
      const res = await addDependenciaApi(idOrder, {
        id_item: idItem,
        id_item_predecesor: idPredecesor,
      });
      await recargar();
      return res;
    },
    [idOrder, recargar]
  );

  const quitarDep = useCallback(
    async (idItem, idPredecesor) => {
      const res = await removeDependenciaApi(idOrder, {
        id_item: idItem,
        id_item_predecesor: idPredecesor,
      });
      await recargar();
      return res;
    },
    [idOrder, recargar]
  );

  return {
    order: summary,
    tareas: summary?.items ?? [],
    dependencias: summary?.dependencias ?? [],
    loading,
    error,
    encender,
    apagar,
    actualizarItem,
    crearItem,
    reordenar,
    agregarDep,
    quitarDep,
    recargar,
  };
}

export default useOrderGantt;
